import type { AgentShape, UserShape } from '../ownership-models.ts';

import type { PageOwnershipByLineWithCollaboratorsDropdownContentQuery$data } from './__generated__/PageOwnershipByLineWithCollaboratorsDropdownContentQuery.graphql.ts';

type CollaboratorsData =
	| PageOwnershipByLineWithCollaboratorsDropdownContentQuery$data['confluence_contentCollaborators']
	| undefined;

export type CollaboratorItem =
	| { kind: 'human'; key: string; user: UserShape }
	| { kind: 'agent'; key: string; agent: AgentShape; user: UserShape };

/** One contributor per person or agent/person pair, with the owner's agents first. */
export const getCollaboratorItems = (
	collaborators: CollaboratorsData,
	ownerId: string,
): CollaboratorItem[] => {
	const ownerAgents: CollaboratorItem[] = [];
	const otherContributors: CollaboratorItem[] = [];
	const seen = new Set<string>();

	for (const [index, node] of (collaborators?.nodes ?? []).entries()) {
		if (!node?.person) {
			continue;
		}
		const { person, agent } = node;
		const isOwner = Boolean(person.accountId && person.accountId === ownerId);
		if (!agent && isOwner) {
			continue;
		}
		// Anonymous identities cannot be safely deduplicated by their display names.
		const personKey = person.accountId ?? `anonymous-${index}`;
		const key = JSON.stringify([personKey, agent?.accountId ?? null]);
		if (seen.has(key)) {
			continue;
		}
		seen.add(key);
		const user: UserShape = {
			isAnonymous: !person.accountId,
			userId: person.accountId ?? '',
			fullName: person.displayName ?? '',
			avatarUrl: person.profilePicture?.path ?? '',
			jobTitle: person.jobTitle ?? null,
		};
		const item: CollaboratorItem = agent
			? {
					kind: 'agent',
					key,
					user,
					agent: {
						accountId: agent.accountId,
						displayName: agent.displayName ?? '',
						avatarUrl: agent.profilePicture?.path ?? '',
					},
				}
			: { kind: 'human', key, user };
		if (agent && isOwner) {
			ownerAgents.push(item);
		} else {
			otherContributors.push(item);
		}
	}

	return [...ownerAgents, ...otherContributors];
};
